// ============================================
// CARTABANCARIACARD.JS — "Carta de datos bancarios" (documento GENERADO), en Datos de la empresa
// ============================================
// v2 (2026-09-09): al generar se ELIGEN las cuentas (ModalCartaBancaria); la carta CADUCA (vigencia por
// defecto configurable) y se puede REVOCAR. El registro muestra el estado de cada carta emitida y deja
// copiar el codigo de verificacion (que ya NO se imprime) para pegarlo en el correo al cliente.
// Todo lo de esta tarjeta es soloAdmin en el backend.

import React, { useCallback, useEffect, useState } from 'react';
import { FileBadge2, Download, ShieldCheck, Copy, Check, Ban } from 'lucide-react';
import { API_BASE_CLI, PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE, estiloBotonPrimario, estiloBotonSecundario } from './helpersClientes';
import { authHeaders as authBase } from '../utils/storage';
import ModalCartaBancaria from './ModalCartaBancaria';

const authHeaders = () => ({ ...authBase(), 'Content-Type': 'application/json' });
const fmt = (v) => { if (!v) return '—'; const d = new Date(v); return isNaN(d) ? '—' : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; };
const ESTADO = {
  vigente: { txt: 'Vigente', bg: '#D3F0DF', color: '#1E7A4C' },
  vencido: { txt: 'Vencida', bg: '#F3F5F8', color: '#5E666E' },
  revocado: { txt: 'Revocada', bg: '#FDF2F2', color: '#B42318' },
};

export default function CartaBancariaCard() {
  const [registro, setRegistro] = useState([]);
  const [correoOk, setCorreoOk] = useState(false);
  const [modal, setModal] = useState(false);
  const [msg, setMsg] = useState(null);
  const [copiado, setCopiado] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_CLI}/api/empresa/documentos/carta-bancaria/registro?limite=8`, { headers: authHeaders() });
      const j = await r.json().catch(() => ({}));
      if (j.ok) { setRegistro(j.registro || []); setCorreoOk(!!j.correo_configurado); }
    } catch { /* silencioso: la tarjeta sigue sirviendo para generar */ }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const copiar = async (d) => {
    try {
      await navigator.clipboard.writeText(`Folio: ${d.folio}\nCódigo de verificación: ${d.codigo_corto}`);
      setCopiado(d.id); setTimeout(() => setCopiado(null), 2500);
    } catch { setMsg({ tipo: 'error', txt: 'No se pudo copiar el código. Anótalo a mano.' }); }
  };

  const revocar = async (d) => {
    if (!window.confirm(`¿Revocar la carta ${d.folio}? Quien la tenga verá "ya no está vigente" al verificarla. No se puede deshacer.`)) return;
    try {
      const r = await fetch(`${API_BASE_CLI}/api/empresa/documentos/carta-bancaria/${d.id}/revocar`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({}) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo revocar');
      setRegistro(j.registro || []);
      setMsg({ tipo: 'ok', txt: `Carta ${d.folio} revocada.` });
    } catch (e) { setMsg({ tipo: 'error', txt: e.message }); }
  };

  return (
    <div style={{ marginTop: 14, border: `1px solid ${COLOR_BORDE}`, borderRadius: 12, padding: 14, background: '#F8FBFF', fontFamily: PP }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, display: 'flex', alignItems: 'center', gap: 8 }}><FileBadge2 size={17} color="#265F97" /> Carta de datos bancarios <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#DEEBF7', color: '#265F97', fontWeight: 700 }}>generada por el sistema</span></div>
        <button type="button" style={{ ...estiloBotonPrimario, marginLeft: 'auto', minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setModal(true)}><Download size={15} /> Generar carta</button>
      </div>
      <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 6, lineHeight: 1.5, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <ShieldCheck size={14} color="#1E7A4C" style={{ marginTop: 2, flex: 'none' }} />
        <span>Eliges qué cuentas van impresas y la carta nace con vigencia (la de Datos de la empresa, o la de la cotización cuando se genera desde ahí). Cada cuenta con carátula bancaria subida lleva su anexo. El código de verificación <strong>no se imprime</strong>{correoOk ? ': viaja en el correo con el que se envía' : ': cópialo aquí y pégalo en el correo que le mandes al cliente'}. Si algo cambia, puedes <strong>revocar</strong> una carta ya enviada.</span>
      </div>
      {msg && <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 600, color: msg.tipo === 'error' ? '#dc2626' : '#16a34a' }}>{msg.txt}</div>}

      {registro.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 6 }}>Cartas emitidas</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {registro.map(r => {
              const est = ESTADO[r.estado] || ESTADO.vencido;
              return (
                <div key={r.id} style={{ border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, padding: 10, background: '#fff' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'monospace', color: COLOR_OSCURO, fontWeight: 700, fontSize: 12.5 }}>{r.folio}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: est.bg, color: est.color, fontWeight: 700 }}>{est.txt}</span>
                    {r.cotizacion_folio && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#DEEBF7', color: '#265F97', fontWeight: 700 }}>Cotización {r.cotizacion_folio}</span>}
                    {r.con_anexo && <span style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>con anexo</span>}
                  </div>
                  <div style={{ fontSize: 12, color: COLOR_GRIS, marginTop: 4, lineHeight: 1.55 }}>
                    Emitida {fmt(r.emitido_at)} por {r.emitido_por_nombre || '—'} · {r.destino === 'descarga' ? 'descarga' : `enviada a ${r.destino}`}<br />
                    {r.estado === 'revocado'
                      ? <>Revocada el {fmt(r.revocado_en)}{r.revocado_por_nombre ? ` por ${r.revocado_por_nombre}` : ''}{r.revocado_motivo ? ` — ${r.revocado_motivo}` : ''}</>
                      : <>Vigente hasta {fmt(r.expira_en)} ({r.vigencia_dias} días)</>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    <button type="button" onClick={() => copiar(r)} style={{ ...estiloBotonSecundario, minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                      {copiado === r.id ? <><Check size={14} color="#1E7A4C" /> Copiado</> : <><Copy size={14} /> Copiar código de verificación</>}
                    </button>
                    {r.estado !== 'revocado' && (
                      <button type="button" onClick={() => revocar(r)} style={{ ...estiloBotonSecundario, minHeight: 44, color: '#B42318', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}><Ban size={14} /> Revocar</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ModalCartaBancaria abierto={modal} onCerrar={() => setModal(false)} onEmitida={cargar} />
    </div>
  );
}
